import { logger } from '@/services/LoggingService';
import { ensureVezgoAccountId, isValidVezgoAccountId } from './type-helpers';

// Simple interface using plain string type instead of branded type
interface ConnectionDetails {
  readonly account: string; // Plain string type - ensures immutability with readonly
  code: string | null;
  state: string | null;
  error: string | null;
  isSuccess: boolean;
}

/**
 * Extract Vezgo callback parameters from URL
 * Uses branded type for account to ensure type safety
 */
export function extractConnectionDetailsFromUrl(): ConnectionDetails | null {
  try {
    const params = new URLSearchParams(window.location.search);
    
    const rawAccount = params.get('account');
    if (!rawAccount) {
      // CRITICAL FIX: Silently return null without logging errors for initial page loads
      // Only log an actual error if we have specific Vezgo parameters but missing the account
      // Check for Vezgo-specific flow and avoid triggering error for other providers like Wealthica
      const isVezgoFlow = params.has('provider') && !params.has('institution');
      
      if (isVezgoFlow && (params.has('code') || params.has('state'))) {
        logger.error('extractConnectionDetailsFromUrl', 'Missing required account parameter', {
          search: window.location.search
        });
      } else {
        // This is likely just an initial page load or another provider's flow (e.g., Wealthica)
        logger.debug('extractConnectionDetailsFromUrl', 'No account parameter (normal for non-Vezgo flow)', {
          search: window.location.search
        });
      }
      return null;
    }

    // CRITICAL FIX: Better type handling to ensure TypeScript recognizes string type
    try {
      // Explicitly convert to string and validate - using stricter type handling
      let accountValue: string;
      
      if (typeof rawAccount === 'number' || (rawAccount && !isNaN(Number(rawAccount)))) {
        // Handle numeric account IDs by explicitly converting to string
        accountValue = String(rawAccount);
        logger.debug('extractConnectionDetailsFromUrl', 'Converted numeric account ID to string', {
          original: rawAccount,
          converted: accountValue
        });
      } else {
        // It's already a string, but ensure TypeScript sees it as one
        accountValue = String(rawAccount);
      }
      
      // Additional validation
      if (!isValidVezgoAccountId(accountValue)) {
        throw new Error(`Invalid account ID format: ${rawAccount}`);
      }
      
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');
      const message = params.get('message');
      
      const isSuccess = message === 'Success' || !!(accountValue && code);
      
      if (!isSuccess && error) {
        logger.error('extractConnectionDetailsFromUrl', 'Connection error in callback', {
          error,
          message
        });
      }

      // Return ConnectionDetails with guaranteed string type
      const result: ConnectionDetails = {
        account: accountValue, // Explicitly typed and guaranteed to be string
        code,
        state,
        error,
        isSuccess
      };
      
      return result;
    } catch (err) {
      logger.error('extractConnectionDetailsFromUrl', 'Invalid account ID format', {
        rawAccount,
        error: err instanceof Error ? err.message : String(err)
      });
      return null;
    }
  } catch (err) {
    logger.error('extractConnectionDetailsFromUrl', 'Error extracting parameters', {
      error: err instanceof Error ? err.message : String(err),
      search: window.location.search
    });
    return null;
  }
}

/**
 * Build Vezgo connect URL with required parameters
 */
export function buildVezgoConnectUrl(
  baseUrl: string,
  clientId: string,
  redirectUri: string,
  token?: string,
  provider?: string,
  state?: string
): string {
  const url = new URL(baseUrl);
  
  // Required parameters
  url.searchParams.append('client_id', clientId);
  url.searchParams.append('redirect_uri', redirectUri);
  
  // Optional parameters
  if (token) {
    url.searchParams.append('token', token);
  }
  
  if (provider) {
    url.searchParams.append('provider', provider);
  }
  
  if (state) {
    url.searchParams.append('state', state);
  }

  logger.debug('buildVezgoConnectUrl', 'Built connection URL', {
    baseUrl,
    clientId,
    redirectUri,
    hasToken: !!token,
    provider,
    state
  });
  
  return url.toString();
}
